// src/utils/analytics.js - Enhanced Analytics with User Journey Tracking
import ReactGA from 'react-ga4';

// Get Google Analytics Measurement ID from environment variables
export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
let isCurrentUserAdmin = false;

export const setUserAdminStatus = (isAdmin) => {
  isCurrentUserAdmin = isAdmin;
};

// Helper function to check if analytics consent is given
const hasAnalyticsConsent = () => {
  const consent = localStorage.getItem('cookieConsent');
  if (!consent) return false;
  
  try {
    const parsedConsent = JSON.parse(consent);
    return parsedConsent.analytics === true;
  } catch (error) {
    console.error('Error parsing cookie consent:', error);
    return false;
  }
};

// Helper function to check if we should track
const shouldTrack = () => {
  const hasConsent = hasAnalyticsConsent();
  const isProductionOrEnabled = process.env.NODE_ENV === 'production' || 
                                process.env.REACT_APP_ENABLE_GA === 'true';

  if (isCurrentUserAdmin) {
    return false;
  }
  
  return hasConsent && isProductionOrEnabled;
};

// Initialize Google Analytics
export const initGA = () => {
  if (shouldTrack()) {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      debug: process.env.NODE_ENV === 'development'
    });
    console.log('📊 Google Analytics initialized with react-ga4');
  } else {
    if (isCurrentUserAdmin) {
      console.log('📊 Google Analytics disabled - admin user');
    } else if (!hasAnalyticsConsent()) {
      console.log('📊 Google Analytics disabled - no user consent');
    } else {
      console.log('📊 Google Analytics disabled - not in production mode');
    }
  }
};

// Track page views
export const trackPageView = (path, title) => {
  if (shouldTrack()) {
    ReactGA.send({ 
      hitType: "pageview", 
      page: path, 
      title: title 
    });
    console.log('📊 Page view tracked:', path);
  }
};

// Track custom events
export const trackEvent = (action, category = 'General', label = '', value = null) => {
  if (shouldTrack()) {
    const eventData = {
      action,
      category,
      label,
    };
    
    if (value !== null) {
      eventData.value = value;
    }
    
    ReactGA.event(eventData);
    console.log('📊 Event tracked:', { action, category, label, value });
  }
};

// Track user authentication events
export const trackAuthEvent = (action, method = '') => {
  trackEvent(action, 'Authentication', method);
};

// Track admin actions
export const trackAdminEvent = (action, resource = '') => {
  trackEvent(action, 'Admin', resource);
};

// Track user engagement
export const trackEngagement = (action, details = '') => {
  trackEvent(action, 'Engagement', details);
};

// ========================================
// EXISTING OFFER TRACKING
// ========================================

export const trackOfferClick = (offerId, offerTitle, offerSource = '', offerReward = '') => {
  trackEvent('offer_click', 'Offers', `${offerId}|${offerTitle}|${offerSource}`, offerReward);
  if (shouldTrack()) {
    console.log('📊 Offer click tracked:', { offerId, offerTitle, offerSource, offerReward });
  }
};

export const trackOfferStart = (offerId, offerTitle, offerSource = '', offerReward = '') => {
  trackEvent('offer_start', 'Offers', `${offerId}|${offerTitle}|${offerSource}`, offerReward);
  if (shouldTrack()) {
    console.log('📊 Offer start tracked:', { offerId, offerTitle, offerSource, offerReward });
  }
};

export const trackOfferView = (offerId, offerTitle, offerSource = '') => {
  trackEvent('offer_view', 'Offers', `${offerId}|${offerTitle}|${offerSource}`);
  if (shouldTrack()) {
    console.log('📊 Offer view tracked:', { offerId, offerTitle, offerSource });
  }
};

// ========================================
// ENHANCED USER JOURNEY TRACKING
// ========================================

/**
 * Track key milestones in user journey
 * Use this to create funnel analysis in GA4
 */

// Track when user completes onboarding steps
export const trackOnboardingStep = (stepNumber, stepName) => {
  if (shouldTrack()) {
    ReactGA.event({
      category: 'User Journey',
      action: 'onboarding_step',
      label: stepName,
      value: stepNumber,
    });
    
    // Also send as a custom parameter for better filtering
    ReactGA.event('onboarding_progress', {
      step_number: stepNumber,
      step_name: stepName,
      timestamp: new Date().toISOString()
    });
    console.log('📊 Onboarding step tracked:', { stepNumber, stepName });
  }
};

// Track first-time actions (key conversion moments)
export const trackFirstTimeAction = (actionName, metadata = {}) => {
  if (shouldTrack()) {
    ReactGA.event('first_time_action', {
      action_name: actionName,
      ...metadata,
      timestamp: new Date().toISOString()
    });
    console.log('📊 First time action tracked:', actionName, metadata);
  }
};

// Track when user completes their profile
export const trackProfileCompletion = (completionPercentage, fieldsCompleted = []) => {
  if (shouldTrack()) {
    ReactGA.event('profile_completion', {
      completion_percentage: completionPercentage,
      fields_completed: fieldsCompleted.join(','),
      is_complete: completionPercentage === 100
    });
    console.log('📊 Profile completion tracked:', completionPercentage + '%');
  }
};

// Track key user milestones
export const trackUserMilestone = (milestoneName, metadata = {}) => {
  if (shouldTrack()) {
    ReactGA.event('user_milestone', {
      milestone_name: milestoneName,
      ...metadata,
      timestamp: new Date().toISOString()
    });
    console.log('📊 User milestone tracked:', milestoneName);
  }
};

// ========================================
// ENHANCED OFFER JOURNEY TRACKING
// ========================================

// Track the full offer journey with stages
export const trackOfferJourney = (stage, offerId, offerData = {}) => {
  if (shouldTrack()) {
    ReactGA.event('offer_journey', {
      journey_stage: stage, // 'view', 'click', 'start', 'complete', 'claim'
      offer_id: offerId,
      offer_title: offerData.title || '',
      offer_reward: offerData.reward || '',
      offer_source: offerData.source || '',
      timestamp: new Date().toISOString()
    });
    console.log('📊 Offer journey tracked:', stage, offerId);
  }
};

// Track offer completion (NEW - for when an offer is completed)
export const trackOfferCompletion = (offerId, offerTitle, reward, timeSpent = null) => {
  if (shouldTrack()) {
    const eventData = {
      category: 'Offers',
      action: 'offer_completed',
      label: `${offerId}|${offerTitle}`,
      value: parseFloat(reward) || 0
    };
    
    ReactGA.event(eventData);
    
    // Also send as custom event with more details
    ReactGA.event('offer_completed', {
      offer_id: offerId,
      offer_title: offerTitle,
      reward: parseFloat(reward) || 0,
      time_spent_seconds: timeSpent,
      timestamp: new Date().toISOString()
    });
    
    console.log('📊 Offer completion tracked:', { offerId, offerTitle, reward });
  }
};

// ========================================
// USER ENGAGEMENT PATTERNS
// ========================================

// Track time spent on key pages
export const trackTimeOnPage = (pageName, timeInSeconds) => {
  if (shouldTrack() && timeInSeconds > 0) {
    ReactGA.event('time_on_page', {
      page_name: pageName,
      time_seconds: timeInSeconds,
      time_bucket: getTimeBucket(timeInSeconds)
    });
    console.log('📊 Time on page tracked:', pageName, timeInSeconds + 's');
  }
};

// Track user session milestones
export const trackSessionMilestone = (sessionNumber) => {
  if (shouldTrack()) {
    ReactGA.event('session_milestone', {
      session_count: sessionNumber,
      is_returning_user: sessionNumber > 1
    });
    console.log('📊 Session milestone tracked:', sessionNumber);
  }
};

// Track user returning after signup
export const trackUserReturn = (daysSinceSignup, isFirstReturn = false) => {
  if (shouldTrack()) {
    ReactGA.event('user_return', {
      days_since_signup: daysSinceSignup,
      is_first_return: isFirstReturn,
      retention_bucket: getRetentionBucket(daysSinceSignup)
    });
    console.log('📊 User return tracked:', { daysSinceSignup, isFirstReturn });
  }
};

// ========================================
// REWARD/EARNINGS TRACKING
// ========================================

// Track when user reaches earning milestones
export const trackEarningMilestone = (totalEarnings, milestone) => {
  if (shouldTrack()) {
    ReactGA.event('earning_milestone', {
      total_earnings: parseFloat(totalEarnings) || 0,
      milestone: milestone, // e.g., 'first_pound', 'five_pounds', 'ten_pounds'
      currency: 'GBP'
    });
    console.log('📊 Earning milestone tracked:', milestone, totalEarnings);
  }
};

// Track payout requests
export const trackPayoutRequest = (amount, method) => {
  if (shouldTrack()) {
    ReactGA.event('payout_request', {
      amount: parseFloat(amount) || 0,
      method: method, // 'paypal', 'bank_transfer', etc.
      currency: 'GBP'
    });
    console.log('📊 Payout request tracked:', { amount, method });
  }
};

// Track payout completion
export const trackPayoutComplete = (amount, method) => {
  if (shouldTrack()) {
    ReactGA.event('payout_complete', {
      amount: parseFloat(amount) || 0,
      method: method,
      currency: 'GBP'
    });
    console.log('📊 Payout complete tracked:', { amount, method });
  }
};

// ========================================
// USER PROPERTY SETTING
// ========================================

// Set user properties for better segmentation
export const setUserProperties = (properties) => {
  if (shouldTrack()) {
    ReactGA.set({
      user_properties: properties
    });
    console.log('📊 User properties set:', properties);
  }
};

// Call this after signup to set initial user properties
export const setInitialUserProperties = (userProfile) => {
  if (shouldTrack()) {
    setUserProperties({
      user_role: userProfile.role || 'user',
      signup_date: userProfile.created_at,
      has_verified_email: userProfile.is_verified || false,
      user_gender: userProfile.gender || 'not_specified',
      referral_source: userProfile.referral_source || 'direct'
    });
    console.log('📊 Initial user properties set');
  }
};

// ========================================
// SEARCH AND FILTER TRACKING
// ========================================

// Track search queries
export const trackSearch = (searchTerm, resultsCount = null) => {
  if (shouldTrack()) {
    ReactGA.event('search', {
      search_term: searchTerm,
      results_count: resultsCount
    });
    console.log('📊 Search tracked:', searchTerm);
  }
};

// Track filter usage
export const trackFilterUsage = (filterType, filterValue) => {
  if (shouldTrack()) {
    ReactGA.event('filter_used', {
      filter_type: filterType,
      filter_value: filterValue
    });
    console.log('📊 Filter tracked:', { filterType, filterValue });
  }
};

// ========================================
// ERROR AND ISSUE TRACKING
// ========================================

// Track errors that users encounter
export const trackError = (errorType, errorMessage, context = '') => {
  if (shouldTrack()) {
    ReactGA.event('error_encountered', {
      error_type: errorType,
      error_message: errorMessage,
      context: context,
      timestamp: new Date().toISOString()
    });
    console.log('📊 Error tracked:', { errorType, errorMessage });
  }
};

// Track when offers fail to load
export const trackOfferLoadError = (offerId, errorMessage) => {
  if (shouldTrack()) {
    ReactGA.event('offer_load_error', {
      offer_id: offerId,
      error_message: errorMessage
    });
    console.log('📊 Offer load error tracked:', offerId);
  }
};

// ========================================
// HELPER FUNCTIONS
// ========================================

const getTimeBucket = (seconds) => {
  if (seconds < 10) return '0-10s';
  if (seconds < 30) return '10-30s';
  if (seconds < 60) return '30-60s';
  if (seconds < 180) return '1-3min';
  if (seconds < 300) return '3-5min';
  return '5min+';
};

const getRetentionBucket = (days) => {
  if (days === 0) return 'day_0';
  if (days === 1) return 'day_1';
  if (days <= 7) return 'week_1';
  if (days <= 14) return 'week_2';
  if (days <= 30) return 'month_1';
  return 'month_2+';
};

// ========================================
// UTILITY: Calculate days since signup
// ========================================

export const calculateDaysSinceSignup = (signupDate) => {
  if (!signupDate) return 0;
  const signup = new Date(signupDate);
  const today = new Date();
  const diffTime = Math.abs(today - signup);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// ========================================
// BATCH EVENT TRACKING
// ========================================

// Track multiple events at once (for complex actions)
export const trackBatchEvents = (events) => {
  if (shouldTrack()) {
    events.forEach(({ eventName, eventParams }) => {
      ReactGA.event(eventName, eventParams);
    });
    console.log('📊 Batch events tracked:', events.length);
  }
};